import React, { FC, Fragment, useState, ChangeEvent, useEffect } from 'react';
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ActionPrice, TenderedActionProductionTemplate } from 'models/organization';
import { usePlanningTemplate } from 'hooks/usePlanningTemplate';
import { useValueTemplate } from 'hooks/useValueTemplate';
import InternalCodeAutocomplete from 'components/lab-settings/action-settings/shared/internal-code-autocomplete.component';
import MaterialMacroMultiSelect from 'components/lab-settings/action-settings/shared/material-macro-multi-select.component';

type TemplateRef = { digitalTemplateId: string | null; physicalTemplateId: string | null };
type TemplateOption = { _id: string; name: string };

type EditTenderedActionFormProps = {
    actionToEdit: ActionPrice;
    onProductionChange: (tenderedAction: TenderedActionProductionTemplate, isValid: boolean) => void;
};
const EditTenderedActionForm: FC<EditTenderedActionFormProps> = ({ actionToEdit, onProductionChange }) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'lab-settings']);
    const { planningTemplates } = usePlanningTemplate();
    const { valueTemplates } = useValueTemplate();
    const [planningTemplate, setPlanningTemplate] = useState<TemplateRef>({
        digitalTemplateId: actionToEdit.productionTemplate?.planningDigitalTemplateId?._id || null,
        physicalTemplateId: actionToEdit.productionTemplate?.planningPhysicalTemplateId?._id || null,
    });
    const [valueTemplate, setValueTemplate] = useState<TemplateRef>({
        digitalTemplateId: actionToEdit.productionTemplate?.valueDigitalTemplateId?._id || null,
        physicalTemplateId: actionToEdit.productionTemplate?.valuePhysicalTemplateId?._id || null,
    });
    const [prodDeductionValue, setProdDeductionValue] = useState<number | undefined>(
        actionToEdit.productionTemplate?.productionDeductionValue,
    );
    const [internalCode, setInternalCode] = useState<string>(actionToEdit.internalCode || '');
    const [materialMacros, setMaterialMacros] = useState<string[]>(
        (actionToEdit.materialMacros || []).map((materialMacro) => materialMacro._id as string),
    );
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        const validationError = validateProdDeductionValue(prodDeductionValue);
        const isValid = !validationError;

        onProductionChange(
            {
                actionId: actionToEdit.action,
                internalCode: internalCode.trim() || undefined,
                productionPlanningTemplate: planningTemplate,
                productionValueTemplate: valueTemplate,
                productionDeductionValue: prodDeductionValue,
                materialMacros,
            },
            isValid,
        );
    }, [planningTemplate, valueTemplate, prodDeductionValue, internalCode, materialMacros]);

    const validateProdDeductionValue = (value?: number): string | undefined => {
        if (value === undefined || value === null) return undefined;

        if (value < 0) {
            return translate('actions-materials:PRODUCTION_DEDUCTION_VALUE_POSITIVE');
        }

        if (!Number.isInteger(value)) {
            return translate('actions-materials:PRODUCTION_DEDUCTION_VALUE_INTEGER');
        }

        return undefined;
    };

    const handlePlanningTemplateChange = (event: SelectChangeEvent, field: keyof TemplateRef) => {
        const value = event.target.value || null;
        setPlanningTemplate((prev) => ({ ...prev, [field]: value }));
    };

    const handleValueTemplateChange = (event: SelectChangeEvent, field: keyof TemplateRef) => {
        const value = event.target.value || null;
        setValueTemplate((prev) => ({ ...prev, [field]: value }));
        if (!value) {
            setProdDeductionValue(undefined);
        }
    };

    const handleProdDeductionValueChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === '') {
            setProdDeductionValue(undefined);
            setError(undefined);
            return;
        }
        setProdDeductionValue(Number(value));
    };

    const handleProdDeductionValueBlur = () => {
        setError(validateProdDeductionValue(prodDeductionValue));
    };

    const hasAnyTemplate =
        planningTemplate.digitalTemplateId ||
        planningTemplate.physicalTemplateId ||
        valueTemplate.digitalTemplateId ||
        valueTemplate.physicalTemplateId;

    return (
        <Fragment>
            <Grid container spacing={1}>
                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <TextField
                            id="action-code"
                            margin="dense"
                            label={translate('actions-materials:CODE')}
                            variant="standard"
                            inputProps={{ readOnly: true }}
                            fullWidth
                            value={actionToEdit.number}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <TextField
                            id="description"
                            margin="dense"
                            label={translate('common:DESCRIPTION')}
                            variant="standard"
                            inputProps={{ readOnly: true }}
                            fullWidth
                            value={actionToEdit.name}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <TextField
                            id="main-category"
                            margin="dense"
                            label={translate('lab-settings:MAIN_CATEGORY')}
                            variant="standard"
                            inputProps={{ readOnly: true }}
                            fullWidth
                            value={actionToEdit.mainCategory}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <TextField
                            id="sub-category"
                            margin="dense"
                            label={translate('lab-settings:SUB_CATEGORY')}
                            variant="standard"
                            inputProps={{ readOnly: true }}
                            fullWidth
                            value={actionToEdit.subCategory}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <InternalCodeAutocomplete value={internalCode} onChange={setInternalCode} />
                    </FormControl>
                </Grid>

                <Grid item sm={12}>
                    <FormControl fullWidth>
                        <MaterialMacroMultiSelect
                            selectedMaterialMacroIds={materialMacros}
                            onChange={setMaterialMacros}
                        />
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl margin="dense" fullWidth>
                        <InputLabel>{translate('lab-settings:DIGITAL_PLAN')}</InputLabel>
                        <Select
                            label={translate('lab-settings:DIGITAL_PLAN')}
                            value={planningTemplate.digitalTemplateId || ''}
                            onChange={(event) => handlePlanningTemplateChange(event, 'digitalTemplateId')}
                        >
                            <MenuItem value="">
                                <em>{translate('actions-materials:NO_PRODUCTION_PLAN')}</em>
                            </MenuItem>
                            {(planningTemplates as TemplateOption[]).map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={6}>
                    <FormControl margin="dense" fullWidth>
                        <InputLabel>{translate('lab-settings:PHYSICAL_PLAN')}</InputLabel>
                        <Select
                            label={translate('lab-settings:PHYSICAL_PLAN')}
                            value={planningTemplate.physicalTemplateId || ''}
                            onChange={(event) => handlePlanningTemplateChange(event, 'physicalTemplateId')}
                        >
                            <MenuItem value="">
                                <em>{translate('actions-materials:NO_PRODUCTION_PLAN')}</em>
                            </MenuItem>
                            {(planningTemplates as TemplateOption[]).map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item sm={6}>
                    <FormControl margin="dense" fullWidth>
                        <InputLabel>{translate('lab-settings:DIGITAL_VALUE_TEMPLATE')}</InputLabel>
                        <Select
                            label={translate('lab-settings:DIGITAL_VALUE_TEMPLATE')}
                            value={valueTemplate.digitalTemplateId || ''}
                            onChange={(event) => handleValueTemplateChange(event, 'digitalTemplateId')}
                        >
                            <MenuItem value="">
                                <em>{translate('actions-materials:NO_VALUE_TEMPLATE')}</em>
                            </MenuItem>
                            {(valueTemplates as TemplateOption[]).map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={6}>
                    <FormControl margin="dense" fullWidth>
                        <InputLabel>{translate('lab-settings:PHYSICAL_VALUE_TEMPLATE')}</InputLabel>
                        <Select
                            label={translate('lab-settings:PHYSICAL_VALUE_TEMPLATE')}
                            value={valueTemplate.physicalTemplateId || ''}
                            onChange={(event) => handleValueTemplateChange(event, 'physicalTemplateId')}
                        >
                            <MenuItem value="">
                                <em>{translate('actions-materials:NO_VALUE_TEMPLATE')}</em>
                            </MenuItem>
                            {(valueTemplates as TemplateOption[]).map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={6}>
                    <FormControl fullWidth>
                        <TextField
                            id="production-deduction-value"
                            margin="dense"
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            label={translate('actions-materials:PRODUCTION_DEDUCTION_VALUE')}
                            value={prodDeductionValue || ''}
                            disabled={!hasAnyTemplate}
                            onChange={handleProdDeductionValueChange}
                            onBlur={handleProdDeductionValueBlur}
                            error={Boolean(error)}
                            helperText={error}
                        />
                    </FormControl>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default EditTenderedActionForm;
